import { PeopleRole } from "../models/peopleCandidate";
import axiosConfig from "./axiosConfig";

export interface HubSpotRefreshResponse {
  status: "pending";
  operationKey: string;
  siteId: string;
  // True when an identical refresh was already in flight and we coalesced onto it.
  deduped?: boolean;
}

// Kick off a HubSpot re-read + re-match for one role as a long-running operation: two
// paced CRM reads plus a model pass sits too close to the 29s gateway ceiling to run
// synchronously. Returns {operationKey, siteId}; the caller polls useOperation(...) and
// refetches the lead on completion.
export const postLeadHubSpotRefresh = async (
  authToken: string,
  leadId: string,
  role: PeopleRole
): Promise<HubSpotRefreshResponse> => {
  const { data } = await axiosConfig.post(
    `/leads/${leadId}/hubspot-refresh`,
    { role },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

// Record the operator confirming or rejecting a fuzzy match. Scoped to the
// (apolloId, contactId) pair so it cannot later read as approval of a different pairing.
export const patchLeadHubSpotDecision = async (
  authToken: string,
  leadId: string,
  role: PeopleRole,
  apolloId: string,
  contactId: string,
  verdict: "confirmed" | "rejected"
): Promise<{ role: PeopleRole; apolloId: string; verdict: string }> => {
  const { data } = await axiosConfig.patch(
    `/leads/${leadId}/hubspot-decision`,
    { role, apolloId, contactId, verdict },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export interface ContactThreadEmail {
  emailId: string;
  subject: string | null;
  body: string | null;
  sentAt: string | null;
  // True when they wrote to us - the strongest signal in the thread.
  inbound: boolean;
}

export interface ContactThreadResponse {
  role: PeopleRole;
  apolloId: string;
  // False when we hold no HubSpot contact for this person at all, which is a different
  // thing from a linked contact with nothing logged against it.
  linked: boolean;
  emails: ContactThreadEmail[];
  error?: string;
}

export const getLeadContactThread = async (
  authToken: string,
  leadId: string,
  role: PeopleRole,
  apolloId: string
): Promise<ContactThreadResponse> => {
  const { data } = await axiosConfig.get(
    `/leads/${leadId}/contact-thread?role=${role}&apolloId=${encodeURIComponent(apolloId)}`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const getHubSpotMatchTrace = async (
  authToken: string,
  subjectRef: string,
  runId: string
) => {
  const { data } = await axiosConfig.get(
    `/leads/hubspot-match-trace?subjectRef=${encodeURIComponent(subjectRef)}&runId=${encodeURIComponent(runId)}`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data.trace;
};
